/**
 * The post-onboarding dashboard hero: the merchant's progress toward real
 * value, front and centre, unlocking card by card as real events arrive.
 *
 * Progression, from nothing to revenue:
 *   activation pending → "waiting for the first shopper" → first try-on
 *   celebrated with the real count → attributed carts/orders unlock →
 *   attributed revenue leads the page.
 *
 * The labelled simulation stays replayable from here (clearly marked), and
 * conversion/lift claims are NOT shown — those stay on the Analytics page
 * behind the existing minimum-cohort gate and its correlational disclaimer.
 */
import { useEffect, useState } from 'react';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Text,
} from '@shopify/polaris';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useLanguage } from '../providers/LanguageProvider';
import { useShopify } from '../providers/AppBridgeProvider';
import type { ApiResponse, EffectivenessReport, UsageStats } from '../types';
import type { UseOnboardingResult } from '../onboarding/useOnboarding';
import SimulationStep from '../onboarding/SimulationStep';
import { formatPrice, getThemeEditorUrl } from '../onboarding/format';

interface ProgressiveHeroProps {
  onboarding: UseOnboardingResult;
  usage: UsageStats | null;
  onOpenAnalytics: () => void;
}

export default function ProgressiveHero({ onboarding, usage, onOpenAnalytics }: ProgressiveHeroProps) {
  const { t, language } = useLanguage();
  const p = t.progressiveDashboard;
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { shop } = useShopify();
  const authenticatedFetch = useAuthenticatedFetch();
  const [report, setReport] = useState<EffectivenessReport | null>(null);
  const [simulationOpen, setSimulationOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);

  const activationVerified = onboarding.state?.activation.verified ?? false;
  const tryOns = usage?.usedTryOns ?? 0;

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch('/api/analytics/effectiveness?windowDays=30')
      .then((res) => res.json())
      .then((json: ApiResponse<EffectivenessReport>) => {
        if (!cancelled && json.success && json.data) setReport(json.data);
      })
      .catch(() => {
        // Advisory data: the hero degrades to usage-only cards.
      });
    return () => {
      cancelled = true;
    };
  }, [authenticatedFetch]);

  const attributedOrders = report?.attribution.attributedOrders ?? 0;
  const attributedRevenue = report?.attribution.attributedRevenue ?? 0;
  const addedToCart = report?.funnel.addedToCart ?? 0;
  const currency = report?.attribution.currency ?? null;
  const revenueDisplay = formatPrice(String(attributedRevenue), currency, locale) ?? `${attributedRevenue}`;

  const simulationProduct = onboarding.storedResult?.product ?? null;

  return (
    <>
      {!activationVerified && (
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {p.activationPendingTitle}
              </Text>
              <Text as="p" variant="bodyMd">
                {p.activationPendingBody}
              </Text>
              <InlineStack gap="300">
                <Button variant="primary" url={getThemeEditorUrl(shop)} external disabled={!shop}>
                  {p.activationPendingAction}
                </Button>
                <Button onClick={() => void onboarding.checkActivation()}>{p.activationCheck}</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      )}

      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            {tryOns > 0 ? (
              <BlockStack gap="100">
                <Text as="h2" variant="headingLg">
                  {p.firstTryonTitle}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {p.firstTryonBody}
                </Text>
              </BlockStack>
            ) : (
              <BlockStack gap="100">
                <Text as="h2" variant="headingLg">
                  {p.waitingTitle}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {p.waitingBody}
                </Text>
              </BlockStack>
            )}

            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                <BlockStack gap="100">
                  <Text as="p" variant="headingXl">
                    {tryOns}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {p.tryonsServed(tryOns)}
                  </Text>
                </BlockStack>
              </Box>
              <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                <BlockStack gap="100">
                  <Text as="p" variant="headingXl">
                    {addedToCart > 0 || attributedOrders > 0 ? String(addedToCart) : '—'}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {p.cartsTitle}
                  </Text>
                  {addedToCart === 0 && attributedOrders === 0 && (
                    <Badge>{p.lockedUntilData}</Badge>
                  )}
                </BlockStack>
              </Box>
              <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                <BlockStack gap="100">
                  <Text as="p" variant="headingXl">
                    {attributedOrders > 0 ? revenueDisplay : '—'}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {p.revenueTitle}
                  </Text>
                  {attributedOrders === 0 && <Badge>{p.lockedUntilData}</Badge>}
                </BlockStack>
              </Box>
            </InlineGrid>

            <InlineStack gap="300" wrap>
              {simulationProduct && tryOns === 0 && (
                <Button onClick={() => setSimulationOpen(true)}>{p.replaySimulation}</Button>
              )}
              {onboarding.storedResult && (
                <Button variant="plain" onClick={() => setResultOpen(true)}>
                  {p.viewMyTryOn}
                </Button>
              )}
              <Button variant="plain" onClick={onOpenAnalytics}>
                {p.viewAnalytics}
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Layout.Section>

      {simulationProduct && (
        <Modal open={simulationOpen} onClose={() => setSimulationOpen(false)} title={t.onboardingFlow.simTitle}>
          <Modal.Section>
            <SimulationStep
              product={{
                title: simulationProduct.title,
                price: simulationProduct.price,
                currencyCode: simulationProduct.currencyCode,
              }}
              onContinue={() => setSimulationOpen(false)}
              embedded
            />
          </Modal.Section>
        </Modal>
      )}

      {onboarding.storedResult && (
        <Modal open={resultOpen} onClose={() => setResultOpen(false)} title={p.myTryOnTitle}>
          <Modal.Section>
            <div className="ph-no-capture" style={{ textAlign: 'center' }}>
              <img
                src={onboarding.storedResult.image}
                alt={t.onboardingFlow.resultAlt}
                style={{ borderRadius: 12, maxHeight: 480, maxWidth: '100%', objectFit: 'contain' }}
              />
            </div>
          </Modal.Section>
        </Modal>
      )}
    </>
  );
}
